import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const PrivacyPolicy = () => {
  const brand = useBrand();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to {brand.name}</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 sm:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 mb-8">Last updated: January 2026</p>

          <div className="prose prose-slate max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <p className="text-slate-600 leading-relaxed">
                {brand.legal.companyName} ("Platform," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your information when you use our services.
              </p>
            </section>

            {/* 1. Information We Collect */}
            <section className="mb-8" id="collection">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Information We Collect</h2>

              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Account Information</h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Email address (required for account creation)</li>
                <li>User ID (automatically generated)</li>
                <li>Authentication tokens</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Design & Product Data</h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Images you upload</li>
                <li>AI prompts you provide for image generation</li>
                <li>Product configurations and design placements</li>
                <li>Saved designs</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Order Information</h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Shipping address (name, street address, city, state, zip code, country)</li>
                <li>Phone number</li>
                <li>Order history and transaction records</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Payment Information</h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Payment processing is handled by Stripe</li>
                <li>We do NOT store credit card numbers or full payment details</li>
                <li>We retain transaction IDs and payment confirmation records</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Technical Data</h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>IP address (used for security and rate limiting)</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Session identifiers</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Affiliate & Commission Data</h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Referral link activity (clicks, conversions)</li>
                <li>Commission earnings and payout history</li>
              </ul>
            </section>

            {/* 2. How We Use Your Information */}
            <section className="mb-8" id="use">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">2. How We Use Your Information</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Provide Services:</strong> Create products, process orders, and fulfill shipments.</li>
                <li><strong>AI Features:</strong> Generate images and analyze designs based on your prompts.</li>
                <li><strong>Process Payments:</strong> Handle transactions and maintain financial records.</li>
                <li><strong>Communication:</strong> Send order confirmations, shipping updates, and important notices.</li>
                <li><strong>Security:</strong> Prevent fraud, abuse, and unauthorized access through rate limiting and IP monitoring.</li>
                <li><strong>Commissions:</strong> Calculate and process creator/affiliate earnings.</li>
                <li><strong>Improve Services:</strong> Analyze usage patterns to enhance our Platform.</li>
                <li><strong>Legal Compliance:</strong> Meet legal obligations and respond to lawful requests.</li>
              </ul>
            </section>

            {/* 3. Information Sharing */}
            <section className="mb-8" id="sharing">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Information Sharing</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We share your information with third-party service providers as necessary to deliver our services:
              </p>

              {/* Third-party categories */}
              <ul className="list-disc pl-6 text-slate-600 space-y-3 mb-4">
                <li>
                  <strong>Print Fulfillment Partners:</strong> We share product data, design images, and shipping addresses
                  with print-on-demand fulfillment providers to manufacture and ship your orders.
                </li>
                <li>
                  <strong>Payment Processors:</strong> We use third-party payment processors to handle transactions securely.
                  Payment information is processed directly by these providers; we do not store your full payment card details.
                </li>
                <li>
                  <strong>AI Service Providers:</strong> We use artificial intelligence services to generate and analyze images.
                  Images and text prompts you provide may be processed by these services.
                </li>
                <li>
                  <strong>Cloud Infrastructure Providers:</strong> We use cloud hosting services to store and process platform data,
                  including your account information, designs, and order history.
                </li>
              </ul>

              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>We do NOT:</strong>
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Sell your personal data to third parties.</li>
                <li>Share your information for third-party marketing without your consent.</li>
                <li>Provide your data to advertisers or data brokers.</li>
              </ul>

              <p className="text-slate-600 leading-relaxed mt-3">
                <strong>Legal Disclosures:</strong> We may disclose your information if required by law, court order,
                or government request, or to protect our rights, property, or safety.
              </p>
            </section>

            {/* 4. Data Security */}
            <section className="mb-8" id="security">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Data Security</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                We implement appropriate technical and organizational measures to protect your data:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Encryption:</strong> All data is transmitted over HTTPS (TLS encryption).</li>
                <li><strong>Secure Infrastructure:</strong> Data is stored on Amazon Web Services with industry-standard security.</li>
                <li><strong>PCI Compliance:</strong> Payment processing is handled by Stripe, which is PCI-DSS compliant.</li>
                <li><strong>Access Controls:</strong> Authentication via AWS Cognito with secure token management.</li>
                <li><strong>Rate Limiting:</strong> Protection against automated abuse and bot attacks.</li>
                <li><strong>IP Blacklisting:</strong> Automatic blocking of suspicious or abusive activity.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                While we strive to protect your information, no method of transmission over the Internet is 100% secure.
                We cannot guarantee absolute security.
              </p>
            </section>

            {/* 5. Data Retention */}
            <section className="mb-8" id="retention">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Data Retention</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                We retain your data for the following periods:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Account Data:</strong> Until you delete your account.</li>
                <li><strong>Order & Transaction Records:</strong> 7 years (for legal and tax compliance).</li>
                <li><strong>Images & Designs:</strong> Until associated products are deleted.</li>
                <li><strong>Commission Records:</strong> Indefinitely (required for payout history and disputes).</li>
                <li><strong>Rate Limit Logs:</strong> Automatically expire within 24 hours.</li>
                <li><strong>IP Blacklist Entries:</strong> Automatically expire within 24 hours.</li>
              </ul>
            </section>

            {/* 6. Your Rights */}
            <section className="mb-8" id="rights">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Your Rights</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Depending on your location, you may have the following rights under applicable privacy laws
                (such as GDPR for EU residents or CCPA for California residents):
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).</li>
                <li><strong>Right to Portability:</strong> Request your data in a structured, commonly used format.</li>
                <li><strong>Right to Object:</strong> Object to certain processing of your data.</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of marketing communications at any time.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                To exercise any of these rights, please contact us at{' '}
                <a href={`mailto:${brand.legal.supportEmail}`} className="text-blue-600 hover:text-blue-700">
                  {brand.legal.supportEmail}
                </a>. We will respond within the timeframes required by applicable law.
              </p>
            </section>

            {/* 7. Cookies & Tracking */}
            <section className="mb-8" id="cookies">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Cookies & Tracking Technologies</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                We use the following cookies and similar technologies:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Authentication Cookies:</strong> Essential cookies to keep you logged in and secure your session.</li>
                <li><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser.</li>
                <li><strong>Referral Cookies:</strong> Track affiliate referrals for up to 30 days.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                <strong>We do NOT use:</strong>
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Third-party advertising or tracking cookies.</li>
                <li>Cross-site tracking technologies.</li>
                <li>Social media tracking pixels.</li>
              </ul>
            </section>

            {/* 8. Children's Privacy */}
            <section className="mb-8" id="children">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed">
                Our Platform is not intended for children under 13 years of age. We do not knowingly collect
                personal information from children under 13. If you are a parent or guardian and believe your child
                has provided us with personal information, please contact us at{' '}
                <a href={`mailto:${brand.legal.supportEmail}`} className="text-blue-600 hover:text-blue-700">
                  {brand.legal.supportEmail}
                </a>{' '}
                and we will take steps to delete such information.
              </p>
            </section>

            {/* 9. International Users */}
            <section className="mb-8" id="international">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. International Data Transfers</h2>
              <p className="text-slate-600 leading-relaxed">
                Our Platform is operated from the United States. If you access our services from outside the United States,
                please be aware that your information will be transferred to, stored, and processed in the United States
                and other countries where our service providers operate. By using our Platform, you consent to the transfer
                of your information to the United States, which may have different data protection laws than your country of residence.
              </p>
            </section>

            {/* 10. California Privacy Rights */}
            <section className="mb-8" id="ccpa">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">10. California Privacy Rights (CCPA)</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Right to Know:</strong> Request information about the categories and specific pieces of personal information we collect.</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information.</li>
                <li><strong>Right to Opt-Out:</strong> We do not sell personal information, so this right does not apply.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                To submit a request, email us at{' '}
                <a href={`mailto:${brand.legal.supportEmail}`} className="text-blue-600 hover:text-blue-700">
                  {brand.legal.supportEmail}
                </a>.
              </p>
            </section>

            {/* 11. Changes to Privacy Policy */}
            <section className="mb-8" id="changes">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Changes to This Privacy Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or for legal,
                operational, or regulatory reasons. We will notify you of material changes by updating the "Last updated"
                date at the top of this page. We encourage you to review this Privacy Policy periodically.
                Your continued use of the Platform after changes are posted constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            {/* 12. Contact Us */}
            <section className="mb-8" id="contact">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">12. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
                please contact us at:
              </p>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-slate-700">
                  <strong>{brand.legal.companyName}</strong>
                </p>
                <p className="text-slate-600 mt-1">
                  Email:{' '}
                  <a href={`mailto:${brand.legal.supportEmail}`} className="text-blue-600 hover:text-blue-700">
                    {brand.legal.supportEmail}
                  </a>
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mt-12 pt-8 border-t border-slate-200">
              <p className="text-slate-500 text-sm italic">
                By using {brand.legal.companyName}, you acknowledge that you have read and understood this Privacy Policy.
                For our full terms, please see our{' '}
                <Link to="/terms" className="text-blue-600 hover:text-blue-700">Terms of Service</Link>.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-slate-500 text-sm">
            &copy; {brand.legal.copyrightYear} {brand.legal.companyName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
